import { useState, useCallback } from 'react';
import { DialogState, DialogType } from '../types/app.types';

export const useDialogState = () => {
  const [dialogs, setDialogs] = useState<DialogState>({
    room: false,
    username: false,
    admin: false,
    locationConsent: false,
    editControls: false,
    canopySidePanel: false
  });

  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [settingCanopyReleaseLocation, setSettingCanopyReleaseLocation] = useState(false);
  const [onboardingRun, setOnboardingRun] = useState(false);
  const [startLocationTrackingCallback, setStartLocationTrackingCallback] = useState<(() => void) | null>(null);

  const openDialog = useCallback((dialogType: DialogType) => {
    setDialogs(prev => ({ ...prev, [dialogType]: true }));
  }, []);

  const closeDialog = useCallback((dialogType: DialogType) => {
    setDialogs(prev => ({ ...prev, [dialogType]: false }));
  }, []);

  const toggleDialog = useCallback((dialogType: DialogType) => {
    setDialogs(prev => ({ ...prev, [dialogType]: !prev[dialogType] }));
  }, []);

  // Location consent dialog helpers
  const showLocationConsent = useCallback((callback: () => void) => {
    setStartLocationTrackingCallback(() => callback);
    openDialog('locationConsent');
  }, [openDialog]);

  const handleLocationConsentAccept = useCallback(() => {
    if (startLocationTrackingCallback) {
      startLocationTrackingCallback();
      setStartLocationTrackingCallback(null);
    }
    closeDialog('locationConsent');
  }, [startLocationTrackingCallback, closeDialog]);

  // Tutorial helpers
  const startTutorial = useCallback(() => {
    setOnboardingRun(true);
  }, []);

  const handleOnboardingStarted = useCallback(() => {
    setOnboardingRun(false);
  }, []);

  // Canopy release location helpers
  const setCanopyCutawayLocation = useCallback(() => {
    closeDialog('canopySidePanel');
    setSettingCanopyReleaseLocation(true);
  }, [closeDialog]);

  const canopyCutawayLocationSet = useCallback(() => {
    setSettingCanopyReleaseLocation(false);
    openDialog('canopySidePanel');
  }, [openDialog]);

  return {
    dialogs,
    showAdminDialog,
    settingCanopyReleaseLocation,
    onboardingRun,
    openDialog,
    closeDialog,
    toggleDialog,
    showLocationConsent,
    handleLocationConsentAccept,
    startTutorial,
    handleOnboardingStarted,
    setCanopyCutawayLocation,
    canopyCutawayLocationSet,
    setShowAdminDialog
  };
};